import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import classNames from 'classnames'
import type { QuoteFile } from '../../types'
import type { StyleClasses } from '../../styles'
import { getS3VideoUrl, withVideoFirstFrame } from '../FileViewer/file-type-utils'

const useStyles = makeStyles<Theme, ResponsiveVideoProps>(
  (_theme) => ({
    root: {},
    video: {
      maxWidth: '100%',
      height: 'auto',
      maxHeight: '100%'
    },
    // Declared so callers can override them via `classes` — the <video>'s
    // className references both, so without these keys the overrides are
    // silently dropped and MUI warns.
    fileRoot: {},
    image: {}
  }),
  { name: 'ResponsiveVideo' }
)

export interface ResponsiveVideoProps {
  classes?: StyleClasses<typeof useStyles>
  file: QuoteFile
  onClick?: (ev: any) => any
  /**
   * Render an inert first-frame thumbnail (for grids) rather than a full player.
   * Uses native `preload="metadata"` so the tile thumbnails straight from the S3
   * binary instead of a derived `/thumbnail/*.jpeg` a migrated video won't have.
   */
  thumbnail?: boolean
}

function ResponsiveVideo(props: ResponsiveVideoProps) {
  const classes = useStyles(props)
  const { onClick, file, thumbnail } = props
  const src = getS3VideoUrl(file)
  // Preflight-checked, not just "computed" (see useVerifiedPosterSrc): a plain
  // `poster={generateThumbnailSrc(...)}` that 404s (the common case for any fresh S3
  // upload — no server-side thumbnail is ever generated per #21745) doesn't fall back
  // to painting the video's own first frame from `preload="metadata"` the way an
  // *absent* poster does in Chrome — the reserved poster area just stays blank. So we
  // only ever hand the <video> a poster URL once we've confirmed it actually resolves.
  const posterSrc = useVerifiedPosterSrc(generateThumbnailSrc({ file }))

  if (thumbnail) {
    // No controls/poster: the browser paints the first frame from the metadata
    // preload, matching the fullscreen gallery (#21642, precedent #21507).
    // (Firefox may not paint a frame until a gesture/seek — an acceptable grey
    // box, not a bug.) tabIndex={-1} keeps this inert tile out of the tab order.
    return (
      <video
        className={classNames(classes.fileRoot, classes.video, classes.image)}
        src={src}
        aria-label={file.title || 'Video'}
        tabIndex={-1}
        muted
        playsInline
        preload="metadata"
        onClick={onClick}
      />
    )
  }

  return (
    <video
      controls
      className={classNames(classes.fileRoot, classes.video, classes.image)}
      // `#t=0.001` nudges iOS/iPadOS WebKit to paint the video's own first frame
      // instead of a black/grey box when the poster hasn't loaded yet (#21623).
      src={withVideoFirstFrame(src)}
      // Only set once useVerifiedPosterSrc has confirmed the legacy thumbnail
      // actually loads — see the comment above and useVerifiedPosterSrc below.
      poster={posterSrc}
      preload="metadata"
      onClick={onClick}
    />
  )
}

/**
 * Returns `candidateSrc` only once a throwaway `<img>` confirms it actually loads;
 * `undefined` while pending or on failure (or when there's no candidate at all). A
 * plain `<img>` load/error signal doesn't require CORS headers to tell success from
 * failure (we never read pixel data), so this works cross-origin against the S3
 * bucket with no extra config. See the `poster` comment in `ResponsiveVideo` for why
 * this check exists instead of just handing the candidate straight to `<video poster>`.
 */
export function useVerifiedPosterSrc(candidateSrc: string | undefined): string | undefined {
  const [verifiedSrc, setVerifiedSrc] = useState<string>()

  useEffect(() => {
    setVerifiedSrc(undefined)
    if (!candidateSrc) return

    let cancelled = false
    const img = new Image()
    img.onload = () => {
      if (!cancelled) setVerifiedSrc(candidateSrc)
    }
    img.onerror = () => {
      if (!cancelled) setVerifiedSrc(undefined)
    }
    img.src = candidateSrc

    return () => {
      cancelled = true
    }
  }, [candidateSrc])

  return verifiedSrc
}

/**
 * `src` is the complete object URL (see `getS3VideoUrl` for why — this mirrors that
 * fix: an earlier version of this function assumed `src` was the bucket base with no
 * key, which doubled the path for any video whose `src` already includes the key, as
 * it always does today). Derive the bucket origin by stripping the known `s3PublicKey`
 * suffix off `src`, then rebuild the legacy thumbnail path from the key. Returns
 * undefined (a safe no-op `poster`) if `src`/`s3PublicKey` are missing or don't agree,
 * so a malformed record just falls through to the `preload="metadata"` first-frame.
 */
function generateThumbnailSrc({ file }: { file: QuoteFile }): string | undefined {
  const { src, s3PublicKey } = file
  if (!src || !s3PublicKey || !src.endsWith(s3PublicKey)) return undefined

  const origin = src.slice(0, src.length - s3PublicKey.length)
  const cleanKey = s3PublicKey.replace('uploads/', '')
  const idIndex = cleanKey.lastIndexOf('/')
  const dist = cleanKey.slice(0, idIndex)
  const id = cleanKey.slice(idIndex)

  // Singular size url
  return `${origin}${dist}/thumbnail${id.split('.')[0]}.jpeg`
}

export default ResponsiveVideo
